import { useCallback } from "react";
import { IconButtonGroup } from "components/Form";
import { adminAssessmentsHooks } from "pages/Admin/TranscriptionSystem/AdminAssessments";
import { currentDateTime, decimalToTime, formatDateTime } from "utils/formatter";
import { markingWordsHooks } from "..";
import { ReactComponent as VisibilityIcon } from 'assets/icons/Visibility.svg';
import { ReactComponent as GoIcon } from 'assets/icons/Go.svg';
import { ReactComponent as CreateTrainingAssessmentIcon } from 'assets/icons/CreateTrainingAssessment.svg';
import { ReactComponent as BackToIcon } from 'assets/icons/BackTo.svg';
import { SvgIcon, Tooltip, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { assessmentVersions, trainingDuplicateAssessments } from "router/routes";
import { StatusCell } from '..'
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cloneAssessmentFetch, releaseStuckStatusFetch } from "store/middlewares/assessment.crud";
import { toast } from "react-toastify";
import { toastConfig } from "config";
import { queryClient as qc } from 'keys';

export const roleStatusCell = {
    accessorKey: "role_name",
    header: "סטטוס",
    cell: StatusCell
}

export const affiliationCell = {
    accessorKey: "affiliation_name",
    header: "בית ספר"
}

export const fileUploadedCell = {
    accessorKey: 'file_uploaded_date',
    header: "מועד ביצוע",
    cell: info => currentDateTime(info.getValue()),
}

export const statusTypeCell = {
    accessorKey: 'status_type',
    header: "סיבת החזרה"
}

export const stuckReasonCell = {
    accessorKey: 'stuck_reason',
    header: "סיבת תקיעות"
}

export const stuckDateCell = {
    accessorKey: 'stuck_date',
    header: "מועד תקיעה",
    cell: info => info.getValue() ? currentDateTime(info.getValue()) : ""
}

export const markingDateCell = {
    accessorKey: "marking_words_last_update_date",
    header: "סימון אחרון",
    cell: info => {
        const date = info.getValue() || info.row.original.marking_words_start_date;
        return currentDateTime(date);
    }
}

export const markingAudioByCell = {
    accessorKey: "audio_cutting_update_by_username",
    header: "סימון אודיו",
    cell: info => info.getValue() ? info.getValue() : ""
}

export const markingByCell = {
    accessorKey: 'marking_words_update_by_username',
    header: 'סומן ע"י',
}

export const markingDurationCell = {
    accessorKey: 'marking_words_duration',
    header: "משך סימון",
    cell: info => decimalToTime(info.getValue()),
}

export const markingStartDateCell = {
    accessorKey: 'marking_words_start_date',
    header: " התחלת סימון",
    cell: info => info.getValue() ? `${formatDateTime(info.getValue()) || ""}` : "",
}

export const markingEndDateCell = {
    accessorKey: 'marking_words_end_date',
    header: " סיום סימון",
    cell: info => info.getValue() ? `${formatDateTime(info.getValue()) || ""}` : "",
}

export const percentageAccuracyCell = {
    accessorKey: 'accuracy_percent',
    header: "אחוז כיסוי",
    cell: ({ row }) => row.original.accuracy_percent_ai ? `${row.original.accuracy_percent_ai?.toFixed(1)}%` : ""
}

export const useGoAssessmentCell = () => {
    const selectAndViewAssessment = adminAssessmentsHooks.useSelectAndMarking()

    return {
        accessorKey: 'enter_marking_words',
        header: "כניסה",
        enableSorting: false,
        cell: ({ row }) => {

            return (
                <Tooltip key={row.id} tooltip="לכניסה" placement="left">
                    <IconButton
                        className="transparent-icon"
                        onClick={() => {
                            selectAndViewAssessment(row.original)
                        }}
                    >
                        <SvgIcon component={GoIcon} inheritViewBox />
                    </IconButton>
                </Tooltip>
            );
        }
    }
}

export const useViewAssessmentCell = () => {
    const selectAndViewAssessment = adminAssessmentsHooks.useSelectAndMarking()

    return {
        accessorKey: 'view_marking_words',
        header: "צפייה",
        enableSorting: false,
        cell: ({ row }) => {

            return (
                <IconButtonGroup
                    color="thirdly4"
                    tooltip="לצפיה"
                    icon={VisibilityIcon}
                    selected={true}
                    action={() => {
                        selectAndViewAssessment(row.original)
                    }}
                />
            )
        }
    }
}

export const useBackToMarkingCell = () => {
    const { action: backToConfirmation, loading: backToConfirmationLoading } = markingWordsHooks.useBackToConfirmation()

    return {
        accessorKey: 'back_to_marking',
        header: "החזר לסימון",
        enableSorting: false,
        cell: ({ row }) => {

            const disabled = backToConfirmationLoading
            return (
                <IconButtonGroup
                    color="thirdly4"
                    tooltip="החזר לסימון"
                    icon={BackToIcon}
                    selected={!disabled}
                    disabled={disabled}
                    action={() => backToConfirmation(row.original, true)}
                />
            )
        }
    }
}

export const useReleaseStuckCell = () => {
    const queryClient = useQueryClient()

    const { mutateAsync: releaseStuck, isPending: releaseStuckLoading } = useMutation({
        mutationFn: releaseStuckStatusFetch,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [qc.adminAssessments] })
        },
        onError: () => {
            toast.error("בעייה בעדכון", toastConfig)
        }
    })

    return {
        accessorKey: 'release_stuck',
        header: "שחרר תקיעות",
        enableSorting: false,
        cell: ({ row }) => {

            const disabled = releaseStuckLoading
            return (
                <IconButtonGroup
                    color="thirdly4"
                    tooltip="שחרר תקיעות"
                    icon={BackToIcon}
                    selected={!disabled}
                    disabled={disabled}
                    action={() => releaseStuck({ id: row.original.id })}
                />
            )
        }
    }
}

export const useVersionsCell = () => {
    const navigate = useNavigate()

    const goAssessmentVersion = useCallback((id) => {
        navigate(`/manage/${assessmentVersions}/${id}`)
    }, [])

    return {
        accessorKey: 'assessment_versions',
        header: "גרסאות",
        enableSorting: false,
        feature: "assessment_versions",
        permissionHOC: "hidden",
        cell: ({ row }) => {

            const disabled = row.original.transcription_version_count < 2

            return (
                <IconButtonGroup
                    color="thirdly4"
                    tooltip="השוואה בין גרסאות"
                    icon={GoIcon}
                    selected={!disabled}
                    disabled={disabled}
                    action={() => goAssessmentVersion(row.original.id)}
                />
            )
        }
    }
}

export const learningUnitCell = {
    accessorKey: 'learning_unit_name',
    header: "מסגרת לימודית",
    enableSorting: false,
    showBlurred: true,
    showBrokenDisplay: true
}

export const useTrainingAssessmentComparisonCell = () => {
    const navigate = useNavigate()

    const goAssessmentVersion = useCallback((id) => {
        navigate(`/manage/${trainingDuplicateAssessments}/comparison/${id}`)
    }, [])

    return {
        accessorKey: 'assessment_versions',
        header: "השווה מול מבדק המקור",
        enableSorting: false,
        feature: "assessment_versions",
        permissionHOC: "hidden",
        cell: ({ row }) => {

            const disabled = row.original.transcription_version_count < 2

            return (
                <IconButtonGroup
                    color="thirdly4"
                    tooltip="השווה מול מבדק המקור"
                    icon={GoIcon}
                    action={() => goAssessmentVersion(row.original.id)}
                />
            )
        }
    }
}

export const useGoClassificationCell = () => {
    const navigate = useNavigate()

    const goClassificationCell = useCallback((id) => {
        navigate(`/manage/${assessmentVersions}/${id}`)
    }, [])

    return {
        accessorKey: 'assessment_versions',
        header: "השוואה עם גרסת המקור",
        enableSorting: false,
        feature: "assessment_versions",
        permissionHOC: "hidden",
        cell: ({ row }) => {

            const disabled = row.original.transcription_version_count < 2

            return (
                <IconButtonGroup
                    color="thirdly4"
                    tooltip="השוואה בין גרסאות"
                    icon={GoIcon}
                    selected={!disabled}
                    disabled={disabled}
                    action={() => goClassificationCell(row.original.id)}
                />
            )
        }
    }
}

export const useCloneAssessmentForLearningCell = () => {
    const isTraining = markingWordsHooks.useIsTraining()

    const { mutateAsync: cloneAss } = useMutation({
        mutationFn: cloneAssessmentFetch,
        onSuccess: () => {
            toast('גרסת הבודקת נוצרה בהצלחה, ניתן למצוא תחת טאב מבדקי לימוד', toastConfig)
        }
    })

    const cloneAssessment = useCallback((id) => {
        cloneAss(id)
    }, [])

    return {
        accessorKey: 'create_training_assessment',
        header: "צור גרסת בודק",
        enableSorting: false,
        feature: "training_assessments",
        role: "write",
        permissionHOC: "hidden",
        cell: ({ row }) => {

            return (
                <IconButtonGroup
                    color="thirdly4"
                    tooltip="צור גרסת בודק"
                    icon={CreateTrainingAssessmentIcon}
                    disabled={isTraining}
                    action={() => cloneAssessment(row.original.id)}
                />
            )
        }
    }
}
